import { useDraggable } from '@dnd-kit/core';
import { Calendar } from 'lucide-react';
import type { Post } from '../types';
import { getStatusColor } from '../constants';

interface PostCardProps {
  post: Post;
  postIdx: number;
  iconSrc: string | undefined;
  onClick: () => void;
}

export function PostCard({ post, postIdx, iconSrc, onClick }: PostCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `post-${post.date}-${post.time}-${postIdx}`,
    data: { post },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  } : { cursor: 'grab' };

  const firstImage = post.attachments?.find(a => a.type === 'image')?.url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="bg-muted border text-foreground text-xs rounded-md shadow-sm hover:bg-accent transition-colors overflow-hidden"
      title={`${post.time} - ${post.topic}: ${post.text}`}
      onClick={onClick}
    >
      {firstImage && (
        <div className="w-full h-12 bg-muted/20 flex items-center justify-center overflow-hidden">
          <img
            src={firstImage}
            alt=""
            className="max-w-full max-h-full object-cover w-full object-centerdfoo"
          />
        </div>
      )}

      <div className="flex items-start gap-1.5 px-2 py-1.5">
        {iconSrc ? (
          <img src={iconSrc} alt="" className="w-3 h-3 flex-shrink-0 mt-0.5" />
        ) : (
          <Calendar size={12} className="flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{post.time}</div>
          <div className="truncate text-muted-foreground text-[10px] leading-tight font-semibold">
            {post.topic}
          </div>
          {post.pageName && (
            <div className="truncate text-muted-foreground text-[9px] leading-tight mt-0.5">
              📄 {post.pageName}
            </div>
          )}
        </div>

        {post.status && (
          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${getStatusColor(post.status)}`} />
        )}
      </div>
    </div>
  );
}
